import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useEstablishment } from '../components/EstablishmentSelector';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
    DropdownMenuSub,
    DropdownMenuSubTrigger,
    DropdownMenuSubContent,
} from '../components/ui/dropdown-menu';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { 
    Plus, 
    Search, 
    Edit, 
    Trash2, 
    Package, 
    MoreVertical,
    History,
    RefreshCw,
    AlertTriangle,
    CheckCircle2,
    XCircle,
    Warehouse,
    LayoutGrid,
    LayoutList,
    ChevronLeft,
    ChevronRight,
    ArrowUpDown,
    ArrowUp,
    ArrowDown,
    PlusCircle,
    MinusCircle,
    Filter,
    Eye,
    ShoppingBag,
    ShoppingCart,
    Coffee,
    Utensils,
    Sparkles,
    Box,
    Droplets,
    Croissant,
    Calendar,
    TrendingDown,
    DollarSign,
    Leaf,
    Wheat,
    Flame,
    Wine,
    Wrench,
    FileText,
    Egg,
    Zap,
    Save
} from 'lucide-react';
import { cn } from '../lib/utils';
import MobilePageHeader from '../components/MobilePageHeader';

// ============================================================
// GESTION DES PRODUITS / STOCK - VERSION TABLEAU COMPACTE
// ============================================================

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount || 0) + ' F';
};

// ============================================================
// TYPES DE PRODUITS (logique métier: recettes, consommation, amortissement)
// ============================================================
const PRODUCT_TYPES = {
    raw_materials: { label: 'Matière première', color: 'bg-blue-100 text-blue-800', icon: Utensils },
    boisson: { label: 'Boisson', color: 'bg-green-100 text-green-800', icon: Coffee },
    consommable: { label: 'Consommable', color: 'bg-purple-100 text-purple-800', icon: Sparkles },
    equipement: { label: 'Équipement', color: 'bg-gray-100 text-gray-800', icon: Wrench },
    non_vendable: { label: 'Non vendable', color: 'bg-slate-100 text-slate-800', icon: Box },
};

// ============================================================
// CATÉGORIES DE PRODUITS (organisation métier, filtres, rapports)
// ============================================================
const PRODUCT_CATEGORIES = {
    'epicerie': { label: 'Épicerie', icon: ShoppingCart },
    'epices-condiments': { label: 'Épices & Condiments', icon: Flame },
    'viandes-poissons': { label: 'Viandes & Poissons', icon: Utensils },
    'legumes': { label: 'Légumes', icon: Leaf },
    'produits-frais': { label: 'Produits Frais', icon: Egg },
    'feculents': { label: 'Féculents', icon: Wheat },
    'huiles-matieres-grasses': { label: 'Huiles & Matières grasses', icon: Droplets },
    'snacks': { label: 'Snacks', icon: Croissant },
    'emballage': { label: 'Emballage', icon: Package },
    'conditionnement': { label: 'Conditionnement', icon: Box },
    'entretien': { label: 'Entretien', icon: Sparkles },
    'papeterie': { label: 'Papeterie', icon: FileText },
    'stock-bar': { label: 'Stock Bar', icon: Wine },
};

// Category icons mapping (for dynamic categories from DB)
const CATEGORY_ICONS = {
    'viandes-poissons': Utensils,
    'legumes': Leaf,
    'feculents': Wheat,
    'epices-condiments': Flame,
    'huiles-matieres-grasses': Droplets,
    'epicerie': ShoppingCart,
    'boissons': Coffee,
    'boissons-bar': Wine,
    'stock-bar': Wine,
    'emballage': Package,
    'conditionnement': Box,
    'entretien': Sparkles,
    'equipement': Wrench,
    'papeterie': FileText,
    'produits-frais': Egg,
    'plats': Utensils,
    'snacks': Croissant,
};

// Unit options
const UNIT_OPTIONS = [
    { value: 'unité', label: 'Unité' },
    { value: 'kg', label: 'Kg' },
    { value: 'g', label: 'Grammes' },
    { value: 'L', label: 'Litres' },
    { value: 'mL', label: 'mL' },
    { value: 'sachet', label: 'Sachet' },
    { value: 'paquet', label: 'Paquet' },
    { value: 'carton', label: 'Carton' },
    { value: 'bouteille', label: 'Bouteille' },
    { value: 'pièce', label: 'Pièce' },
    { value: 'lot', label: 'Lot' },
    { value: 'rouleau', label: 'Rouleau' },
    { value: 'bidon', label: 'Bidon' },
    { value: 'boîte', label: 'Boîte' },
];

// Daily stock items (frequently restocked)
const DAILY_STOCK_KEYWORDS = ['baguette', 'pain', 'oeuf', 'œuf', 'eau', 'jus', 'coca', 'fanta', 'sprite', 'lait', 'yaourt', 'fromage', 'beurre', 'croissant'];

// Get stock status
const getStockStatus = (quantity, threshold) => {
    const qty = parseFloat(quantity) || 0;
    const thresh = parseFloat(threshold) || 3;
    if (qty <= 0) return { status: 'rupture', label: 'RUPTURE', color: 'bg-red-500 text-white', rowColor: 'bg-red-50' };
    if (qty <= thresh) return { status: 'faible', label: 'FAIBLE', color: 'bg-orange-500 text-white', rowColor: 'bg-orange-50' };
    return { status: 'ok', label: 'OK', color: 'bg-green-500 text-white', rowColor: '' };
};

// Get product type from product data
const getProductType = (product) => {
    // Use actual product_type from backend
    const type = product.product_type || product.productType;
    if (type && PRODUCT_TYPES[type]) {
        return type;
    }
    // Handle legacy types - map to new structure
    if (type === 'durable') return 'equipement';
    if (type === 'emballage' || type === 'entretien') return 'non_vendable';
    // Fallback to raw_materials for undefined
    return 'raw_materials';
};

// Summary Card Component
const SummaryCard = ({ title, value, icon: Icon, color, subtext }) => (
    <div className={cn("flex items-center gap-3 p-3 rounded-lg border", color)}>
        <div className={cn("p-2 rounded-lg", color.includes('red') ? 'bg-red-100' : color.includes('orange') ? 'bg-orange-100' : color.includes('green') ? 'bg-green-100' : 'bg-blue-100')}>
            <Icon className="w-4 h-4" />
        </div>
        <div>
            <p className="text-xs text-muted-foreground">{title}</p>
            <p className="text-lg font-bold">{value}</p>
            {subtext && <p className="text-xs text-muted-foreground">{subtext}</p>}
        </div>
    </div>
);

// Filter Button Component
const FilterButton = ({ active, onClick, icon: Icon, label, count }) => (
    <button
        onClick={onClick}
        className={cn(
            "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap",
            active 
                ? "bg-primary text-primary-foreground shadow-sm" 
                : "bg-muted hover:bg-muted/80 text-muted-foreground"
        )}
    >
        {Icon && <Icon className="w-3.5 h-3.5" />}
        {label}
        {count !== undefined && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-white/20 text-[10px]">{count}</span>}
    </button>
);

// Quick Stock Inline Component
const QuickStockInline = ({ product, onStockChange }) => {
    const [qty, setQty] = useState('1');
    
    return (
        <div className="flex items-center gap-1">
            <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 bg-red-100 hover:bg-red-200"
                onClick={() => onStockChange(product, 'out', parseFloat(qty) || 1)}
            >
                <MinusCircle className="w-3.5 h-3.5 text-red-600" />
            </Button>
            <Input
                type="number"
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                className="w-12 h-6 text-xs text-center px-1"
                min="1"
            />
            <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 bg-green-100 hover:bg-green-200"
                onClick={() => onStockChange(product, 'in', parseFloat(qty) || 1)}
            >
                <PlusCircle className="w-3.5 h-3.5 text-green-600" />
            </Button>
        </div>
    );
};

// Main Component
const ProductsPage = () => {
    const { api, hasPermission } = useAuth();
    const { selectedEstablishmentId } = useEstablishment();
    
    // Data state
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    
    // View state
    const [viewMode, setViewMode] = useState('table'); // 'table' | 'cards'
    const [activeTab, setActiveTab] = useState('all'); // 'all' | 'daily'
    
    // Filter state
    const [searchTerm, setSearchTerm] = useState('');
    const [activeFilter, setActiveFilter] = useState('all');
    const [stockFilter, setStockFilter] = useState('all'); // 'all' | 'faible' | 'rupture'
    const [showAllStock, setShowAllStock] = useState(false);
    
    // Sort state
    const [sortField, setSortField] = useState('name');
    const [sortDirection, setSortDirection] = useState('asc');
    
    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(100);
    
    // Dialogs
    const [dialogOpen, setDialogOpen] = useState(false);
    const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
    const [quickStockDialogOpen, setQuickStockDialogOpen] = useState(false);
    const [quickRestockDialogOpen, setQuickRestockDialogOpen] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [productHistory, setProductHistory] = useState([]);
    const [stockAction, setStockAction] = useState('in');
    const [stockQuantity, setStockQuantity] = useState('1');
    const [stockReason, setStockReason] = useState('restock');
    const [stockNotes, setStockNotes] = useState('');
    
    // Quick Restock state
    const [restockItems, setRestockItems] = useState({});
    const [restockMode, setRestockMode] = useState(false);
    
    // Product form
    const [formData, setFormData] = useState({
        name: '',
        category_id: '',
        unit_base: 'unité',
        purchase_price_cfa: '',
        is_sellable: true,
        selling_price_cfa: '',
        stock_quantity_base: '0',
        alert_threshold_base: '3',
        supplier: '',
    });
    
    // Permissions
    const canViewCosts = hasPermission?.('view_purchase_price') ?? true;
    const canEdit = hasPermission?.('edit_product') ?? true;
    const canDelete = hasPermission?.('delete_product') ?? true;
    
    // Fetch data
    const fetchData = useCallback(async () => {
        try {
            setRefreshing(true);
            const productParams = selectedEstablishmentId 
                ? `/products?establishment_id=${selectedEstablishmentId}` 
                : '/products';
            
            const [productsRes, categoriesRes] = await Promise.all([
                api().get(productParams),
                api().get('/categories'),
            ]);
            
            setProducts(productsRes.data || []);
            setCategories(categoriesRes.data || []);
        } catch (error) {
            toast.error('Erreur lors du chargement des données');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [api, selectedEstablishmentId]);
    
    useEffect(() => {
        fetchData();
    }, [fetchData]);
    
    // Category map for quick lookup
    const categoryMap = useMemo(() => {
        const map = {};
        categories.forEach(cat => { map[cat.id] = cat; });
        return map;
    }, [categories]);
    
    // Dynamic category filters based on actual categories in inventory
    const dynamicCategoryFilters = useMemo(() => {
        // Get categories that have products
        const categoriesWithProducts = new Set(products.map(p => p.category_id).filter(Boolean));
        
        // Create filter buttons for each category
        const filters = [{ id: 'all', label: 'Tous', icon: Package, count: products.length }];
        
        categories
            .filter(cat => categoriesWithProducts.has(cat.id))
            .sort((a, b) => a.name.localeCompare(b.name))
            .forEach(cat => {
                const count = products.filter(p => p.category_id === cat.id).length;
                filters.push({
                    id: cat.id,
                    label: cat.name,
                    icon: CATEGORY_ICONS[cat.id] || Package,
                    count,
                    color: cat.color
                });
            });
        
        return filters;
    }, [categories, products]);
    
    // Enrich products with computed fields
    const enrichedProducts = useMemo(() => {
        return products.map(p => {
            const category = categoryMap[p.category_id];
            const categoryName = category?.name || '';
            const stockStatus = getStockStatus(p.stock_quantity_base, p.alert_threshold_base);
            const productType = getProductType(p, categoryName);
            const isDailyStock = DAILY_STOCK_KEYWORDS.some(kw => 
                p.name?.toLowerCase().includes(kw) || categoryName.toLowerCase().includes(kw)
            );
            
            return {
                ...p,
                categoryName,
                stockStatus,
                productType,
                isDailyStock,
            };
        });
    }, [products, categoryMap]);
    
    // Summary statistics
    const stats = useMemo(() => {
        const total = enrichedProducts.length;
        const rawMaterials = enrichedProducts.filter(p => p.productType === 'raw_materials').length;
        const boissons = enrichedProducts.filter(p => p.productType === 'boisson').length;
        const consommables = enrichedProducts.filter(p => p.productType === 'consommable').length;
        const nonDefini = enrichedProducts.filter(p => p.productType === 'non_defini').length;
        const faible = enrichedProducts.filter(p => p.stockStatus.status === 'faible').length;
        const rupture = enrichedProducts.filter(p => p.stockStatus.status === 'rupture').length;
        const totalValue = enrichedProducts.reduce((sum, p) => 
            sum + ((parseFloat(p.stock_quantity_base) || 0) * (parseFloat(p.purchase_price_cfa) || 0)), 0
        );
        
        return { total, rawMaterials, boissons, consommables, nonDefini, faible, rupture, totalValue };
    }, [enrichedProducts]);
    
    // Filtered products
    const filteredProducts = useMemo(() => {
        let result = [...enrichedProducts];
        
        // Tab filter (daily stock)
        if (activeTab === 'daily') {
            result = result.filter(p => p.isDailyStock);
        }
        
        // Type/Category filter - now uses category_id directly
        if (activeFilter !== 'all') {
            result = result.filter(p => p.category_id === activeFilter);
        }
        
        // Stock filter
        if (stockFilter === 'faible') {
            result = result.filter(p => p.stockStatus.status === 'faible');
        } else if (stockFilter === 'rupture') {
            result = result.filter(p => p.stockStatus.status === 'rupture');
        }
        
        // Search filter
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            result = result.filter(p => 
                p.name?.toLowerCase().includes(term) ||
                p.categoryName?.toLowerCase().includes(term) ||
                PRODUCT_TYPES[p.productType]?.label.toLowerCase().includes(term) ||
                p.supplier?.toLowerCase().includes(term)
            );
        }
        
        // Sorting
        result.sort((a, b) => {
            let aVal, bVal;
            switch (sortField) {
                case 'stock':
                    aVal = parseFloat(a.stock_quantity_base) || 0;
                    bVal = parseFloat(b.stock_quantity_base) || 0;
                    break;
                case 'purchase_price':
                    aVal = parseFloat(a.purchase_price_cfa) || 0;
                    bVal = parseFloat(b.purchase_price_cfa) || 0;
                    break;
                case 'selling_price':
                    aVal = parseFloat(a.selling_price_cfa) || 0;
                    bVal = parseFloat(b.selling_price_cfa) || 0;
                    break;
                case 'category':
                    aVal = a.categoryName || '';
                    bVal = b.categoryName || '';
                    break;
                case 'status':
                    const statusOrder = { rupture: 0, faible: 1, ok: 2 };
                    aVal = statusOrder[a.stockStatus.status];
                    bVal = statusOrder[b.stockStatus.status];
                    break;
                case 'updated':
                    aVal = new Date(a.updated_at || 0).getTime();
                    bVal = new Date(b.updated_at || 0).getTime();
                    break;
                default: // name
                    aVal = a.name?.toLowerCase() || '';
                    bVal = b.name?.toLowerCase() || '';
            }
            
            if (typeof aVal === 'string') {
                return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
            }
            return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
        });
        
        return result;
    }, [enrichedProducts, activeTab, activeFilter, stockFilter, searchTerm, sortField, sortDirection]);
    
    // Pagination
    const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
    const paginatedProducts = useMemo(() => {
        if (showAllStock) return filteredProducts;
        const start = (currentPage - 1) * itemsPerPage;
        return filteredProducts.slice(start, start + itemsPerPage);
    }, [filteredProducts, currentPage, itemsPerPage, showAllStock]);
    
    // Reset page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, activeFilter, stockFilter, activeTab]);
    
    // Handle sort
    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };
    
    // Sort icon
    const SortIcon = ({ field }) => {
        if (sortField !== field) return <ArrowUpDown className="w-3 h-3 opacity-50" />;
        return sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />;
    };
    
    // Quick stock change
    const handleQuickStockChange = async (product, action, quantity) => {
        if (quantity <= 0) return;
        
        if (action === 'out') {
            const currentStock = parseFloat(product.stock_quantity_base) || 0;
            if (quantity > currentStock) {
                toast.error('Stock insuffisant');
                return;
            }
        }
        
        try {
            await api().post('/stock/movements', {
                product_id: product.id,
                type: action === 'in' ? 'IN' : 'OUT',
                quantity: quantity,
                reason: action === 'in' ? 'restock' : 'usage',
                notes: `Ajustement rapide ${action === 'in' ? '+' : '-'}${quantity}`
            });
            
            toast.success(`${action === 'in' ? '+' : '-'}${quantity} ${product.name}`);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };
    
    // Open stock dialog
    const openStockDialog = (product, action) => {
        setSelectedProduct(product);
        setStockAction(action);
        setStockQuantity('1');
        setStockReason(action === 'in' ? 'restock' : 'usage');
        setStockNotes('');
        setQuickStockDialogOpen(true);
    };
    
    // Handle stock movement
    const handleStockMovement = async () => {
        if (!selectedProduct) return;
        
        const qty = parseFloat(stockQuantity) || 0;
        if (qty <= 0) {
            toast.error('Quantité invalide');
            return;
        }
        
        if (stockAction === 'out') {
            const currentStock = parseFloat(selectedProduct.stock_quantity_base) || 0;
            if (qty > currentStock) {
                toast.error('Stock insuffisant');
                return;
            }
        }
        
        try {
            await api().post('/stock/movements', {
                product_id: selectedProduct.id,
                type: stockAction === 'in' ? 'IN' : 'OUT',
                quantity: qty,
                reason: stockReason,
                notes: stockNotes || `${stockAction === 'in' ? 'Entrée' : 'Sortie'} stock`
            });
            
            toast.success(`Stock ${stockAction === 'in' ? 'ajouté' : 'retiré'}: ${qty} ${selectedProduct.unit_base}`);
            setQuickStockDialogOpen(false);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };
    
    // View history
    const viewProductHistory = async (product) => {
        setSelectedProduct(product);
        try {
            const response = await api().get(`/stock/movements?product_id=${product.id}&limit=50`);
            setProductHistory(response.data || []);
            setHistoryDialogOpen(true);
        } catch (error) {
            toast.error('Erreur lors du chargement de l\'historique');
        }
    };
    
    // Quick Restock functions
    const handleRestockQuantityChange = (productId, delta) => {
        setRestockItems(prev => {
            const current = prev[productId] || 0;
            const newValue = Math.max(0, current + delta);
            if (newValue === 0) {
                const { [productId]: _, ...rest } = prev;
                return rest;
            }
            return { ...prev, [productId]: newValue };
        });
    };
    
    const handleRestockInputChange = (productId, value) => {
        const numValue = parseInt(value) || 0;
        setRestockItems(prev => {
            if (numValue === 0) {
                const { [productId]: _, ...rest } = prev;
                return rest;
            }
            return { ...prev, [productId]: numValue };
        });
    };
    
    const submitQuickRestock = async () => {
        const itemsToRestock = Object.entries(restockItems).filter(([_, qty]) => qty > 0);
        
        if (itemsToRestock.length === 0) {
            toast.warning('Aucun produit à réapprovisionner');
            return;
        }
        
        try {
            let successCount = 0;
            
            for (const [productId, quantity] of itemsToRestock) {
                await api().post(`/stock/movements`, {
                    product_id: productId,
                    type: 'IN',
                    quantity: quantity,
                    reason: 'restock',
                    notes: 'Réapprovisionnement rapide'
                });
                successCount++;
            }
            
            toast.success(`${successCount} produit(s) réapprovisionné(s) avec succès`);
            setRestockItems({});
            setQuickRestockDialogOpen(false);
            fetchData();
        } catch (error) {
            toast.error('Erreur lors du réapprovisionnement');
        }
    };
    
    // Products grouped by category for quick restock
    const productsByCategory = useMemo(() => {
        const grouped = {};
        enrichedProducts.forEach(product => {
            const catId = product.category_id || 'autres';
            if (!grouped[catId]) {
                const cat = categoryMap[catId];
                grouped[catId] = {
                    id: catId,
                    name: cat?.name || 'Autres',
                    color: cat?.color || '#6B7280',
                    products: []
                };
            }
            grouped[catId].products.push(product);
        });
        
        // Sort products within each category
        Object.values(grouped).forEach(cat => {
            cat.products.sort((a, b) => a.name.localeCompare(b.name));
        });
        
        return Object.values(grouped).sort((a, b) => a.name.localeCompare(b.name));
    }, [enrichedProducts, categoryMap]);
    
    // Change product type
    const handleChangeProductType = async (product, newType) => {
        try {
            // Determine is_sellable based on type
            const isSellable = newType === 'boisson';
            
            await api().put(`/products/${product.id}`, {
                product_type: newType,
                is_sellable: isSellable
            });
            
            toast.success(`Type modifié: ${PRODUCT_TYPES[newType]?.label || newType}`);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
        }
    };
    
    // Change product category
    const handleChangeCategoryId = async (product, newCategoryId) => {
        try {
            const category = categories.find(c => c.id === newCategoryId);
            
            await api().put(`/products/${product.id}`, {
                category_id: newCategoryId
            });
            
            toast.success(`Catégorie modifiée: ${category?.name || newCategoryId}`);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
        }
    };
    
    // Product form handlers
    const resetForm = () => {
        setFormData({
            name: '',
            category_id: '',
            unit_base: 'unité',
            purchase_price_cfa: '',
            is_sellable: true,
            selling_price_cfa: '',
            stock_quantity_base: '0',
            alert_threshold_base: '3',
            supplier: '',
        });
    };
    
    const openEditDialog = (product) => {
        setEditingProduct(product);
        setFormData({
            name: product.name || '',
            category_id: product.category_id || '',
            unit_base: product.unit_base || 'unité',
            purchase_price_cfa: product.purchase_price_cfa?.toString() || '',
            is_sellable: product.is_sellable !== false,
            selling_price_cfa: product.selling_price_cfa?.toString() || '',
            stock_quantity_base: product.stock_quantity_base?.toString() || '0',
            alert_threshold_base: product.alert_threshold_base?.toString() || '3',
            supplier: product.supplier || '',
        });
        setDialogOpen(true);
    };
    
    const handleSaveProduct = async () => {
        if (!formData.name) {
            toast.error('Le nom est requis');
            return;
        }
        
        try {
            const data = {
                ...formData,
                purchase_price_cfa: parseFloat(formData.purchase_price_cfa) || 0,
                selling_price_cfa: parseFloat(formData.selling_price_cfa) || 0,
                stock_quantity_base: parseFloat(formData.stock_quantity_base) || 0,
                alert_threshold_base: parseFloat(formData.alert_threshold_base) || 3,
                establishment_id: selectedEstablishmentId
            };
            
            if (editingProduct) {
                await api().put(`/products/${editingProduct.id}`, data);
                toast.success('Produit modifié');
            } else {
                await api().post('/products', data);
                toast.success('Produit créé');
            }
            
            setDialogOpen(false);
            setEditingProduct(null);
            resetForm();
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };
    
    const handleDeleteProduct = async (product) => {
        if (!confirm(`Supprimer "${product.name}" ?`)) return;
        
        try {
            await api().delete(`/products/${product.id}`);
            toast.success('Produit supprimé');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };
    
    // Format date
    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };
    
    // Stock reasons
    const stockInReasons = [
        { value: 'restock', label: 'Réapprovisionnement' },
        { value: 'return', label: 'Retour' },
        { value: 'adjustment', label: 'Ajustement inventaire' },
    ];
    
    const stockOutReasons = [
        { value: 'usage', label: 'Utilisation' },
        { value: 'vente', label: 'Vente' },
        { value: 'casse', label: 'Casse / Perte' },
        { value: 'transfert', label: 'Transfert' },
    ];
    
    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <RefreshCw className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }
    
    return (
        <div className="space-y-4 p-4 md:p-6 pb-20" data-testid="products-inventory-page">
            {/* Mobile Page Header */}
            <MobilePageHeader 
                title="Gestion du Stock"
                subtitle={`${stats.total} produits`}
                icon={Package}
                backPath="/dashboard"
            />
            
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <div>
                    <h1 className="text-xl font-bold">Gestion du Stock</h1>
                    <p className="text-xs text-muted-foreground">Inventaire complet • {stats.total} produits</p>
                </div>
                
                <div className="flex items-center gap-2">
                    {/* View Toggle */}
                    <div className="flex items-center border rounded-lg p-0.5">
                        <Button
                            variant={viewMode === 'table' ? 'default' : 'ghost'}
                            size="sm"
                            className="h-7 px-2"
                            onClick={() => setViewMode('table')}
                        >
                            <LayoutList className="w-4 h-4" />
                        </Button>
                        <Button
                            variant={viewMode === 'cards' ? 'default' : 'ghost'}
                            size="sm"
                            className="h-7 px-2"
                            onClick={() => setViewMode('cards')}
                        >
                            <LayoutGrid className="w-4 h-4" />
                        </Button>
                    </div>
                    
                    <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={fetchData}
                        disabled={refreshing}
                        className="h-8"
                    >
                        <RefreshCw className={cn("w-4 h-4", refreshing && 'animate-spin')} />
                    </Button>
                    
                    {canEdit && (
                        <Button 
                            onClick={() => { resetForm(); setEditingProduct(null); setDialogOpen(true); }}
                            size="sm"
                            className="h-8"
                        >
                            <Plus className="w-4 h-4 mr-1" />
                            Nouveau
                        </Button>
                    )}
                </div>
            </div>
            
            {/* Summary Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                <SummaryCard title="Total produits" value={stats.total} icon={Package} color="border-blue-200" />
                <SummaryCard title="Vendables" value={stats.vendable} icon={ShoppingBag} color="border-green-200" />
                <SummaryCard title="Non vendables" value={stats.nonVendable} icon={Box} color="border-gray-200" />
                <SummaryCard 
                    title="Stock faible" 
                    value={stats.faible} 
                    icon={AlertTriangle} 
                    color={stats.faible > 0 ? "border-orange-400 bg-orange-50" : "border-orange-200"} 
                />
                <SummaryCard 
                    title="En rupture" 
                    value={stats.rupture} 
                    icon={XCircle} 
                    color={stats.rupture > 0 ? "border-red-400 bg-red-50" : "border-red-200"} 
                />
                {canViewCosts && (
                    <SummaryCard 
                        title="Valeur stock" 
                        value={formatCurrency(stats.totalValue)} 
                        icon={DollarSign} 
                        color="border-purple-200" 
                    />
                )}
            </div>
            
            {/* Tabs for All / Daily Stock */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="h-8">
                    <TabsTrigger value="all" className="text-xs h-7">
                        <Warehouse className="w-3.5 h-3.5 mr-1" />
                        Tout le stock
                    </TabsTrigger>
                    <TabsTrigger value="daily" className="text-xs h-7">
                        <Calendar className="w-3.5 h-3.5 mr-1" />
                        Stock du jour
                    </TabsTrigger>
                </TabsList>
            </Tabs>
            
            {/* Filters Row - Responsive */}
            <div className="flex flex-col gap-3">
                {/* Top Row: Search + Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                    {/* Search */}
                    <div className="relative flex-1 min-w-0">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                            placeholder="Rechercher par nom, catégorie..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-8 h-9 text-sm w-full"
                            data-testid="search-input"
                        />
                    </div>
                    
                    {/* Action Buttons - Wrap on mobile */}
                    <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                        {/* Stock Status Filters */}
                        <div className="flex items-center gap-1">
                            <Button
                                variant={stockFilter === 'all' ? 'default' : 'outline'}
                                size="sm"
                                className="h-8 text-xs px-2 sm:px-3"
                                onClick={() => setStockFilter('all')}
                            >
                                Tous
                            </Button>
                            <Button
                                variant={stockFilter === 'faible' ? 'default' : 'outline'}
                                size="sm"
                                className={cn(
                                    "h-8 text-xs px-2 sm:px-3",
                                    stats.faible > 0 && stockFilter !== 'faible' && "border-orange-300 text-orange-600"
                                )}
                                onClick={() => setStockFilter('faible')}
                            >
                                <AlertTriangle className="w-3.5 h-3.5 mr-1" />
                                <span className="hidden sm:inline">Faible</span>
                                {stats.faible > 0 && (
                                    <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">
                                        {stats.faible}
                                    </Badge>
                                )}
                            </Button>
                            <Button
                                variant={stockFilter === 'rupture' ? 'default' : 'outline'}
                                size="sm"
                                className={cn(
                                    "h-8 text-xs px-2 sm:px-3",
                                    stats.rupture > 0 && stockFilter !== 'rupture' && "border-red-300 text-red-600"
                                )}
                                onClick={() => setStockFilter('rupture')}
                            >
                                <XCircle className="w-3.5 h-3.5 mr-1" />
                                <span className="hidden sm:inline">Rupture</span>
                                {stats.rupture > 0 && (
                                    <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">
                                        {stats.rupture}
                                    </Badge>
                                )}
                            </Button>
                        </div>
                        
                        {/* Divider */}
                        <div className="hidden sm:block w-px h-6 bg-border" />
                        
                        {/* View Toggle */}
                        <Button
                            variant={showAllStock ? 'default' : 'outline'}
                            size="sm"
                            className="h-8 text-xs px-2 sm:px-3"
                            onClick={() => setShowAllStock(!showAllStock)}
                        >
                            <Eye className="w-3.5 h-3.5 sm:mr-1" />
                            <span className="hidden sm:inline">{showAllStock ? 'Paginé' : 'Voir tout'}</span>
                        </Button>
                        
                        {/* Quick Restock Button */}
                        <Button
                            variant="default"
                            size="sm"
                            className="h-8 text-xs px-2 sm:px-3 bg-emerald-600 hover:bg-emerald-700"
                            onClick={() => setQuickRestockDialogOpen(true)}
                        >
                            <Zap className="w-3.5 h-3.5 sm:mr-1" />
                            <span className="hidden sm:inline">Réappro. rapide</span>
                        </Button>
                    </div>
                </div>
                
                {/* Category Filters - Scrollable with proper containment */}
                <div className="relative -mx-4 px-4 sm:mx-0 sm:px-0">
                    <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-muted scrollbar-track-transparent pb-2">
                        <div className="flex items-center gap-1.5 min-w-max pr-4 sm:pr-0">
                            {dynamicCategoryFilters.map(filter => {
                                const IconComponent = filter.icon;
                                const isActive = activeFilter === filter.id;
                                return (
                                    <Button
                                        key={filter.id}
                                        variant={isActive ? 'default' : 'outline'}
                                        size="sm"
                                        className={cn(
                                            "h-8 text-xs whitespace-nowrap flex items-center gap-1.5 shrink-0 px-2.5",
                                            isActive ? "shadow-sm" : "hover:bg-muted/50"
                                        )}
                                        style={isActive && filter.color ? { backgroundColor: filter.color } : {}}
                                        onClick={() => setActiveFilter(filter.id)}
                                    >
                                        <IconComponent className="w-3.5 h-3.5" />
                                        <span className="max-w-[100px] truncate">{filter.label}</span>
                                        {filter.count !== undefined && (
                                            <Badge 
                                                variant={isActive ? "outline" : "secondary"} 
                                                className={cn(
                                                    "ml-0.5 h-5 min-w-[20px] px-1.5 text-[10px] font-medium",
                                                    isActive && "bg-white/20 text-white border-white/30"
                                                )}
                                            >
                                                {filter.count}
                                            </Badge>
                                        )}
                                    </Button>
                                );
                            })}
                        </div>
                    </div>
                    {/* Gradient fade indicators */}
                    <div className="absolute right-0 top-0 bottom-2 w-8 bg-gradient-to-l from-background to-transparent pointer-events-none sm:hidden" />
                </div>
            </div>
            
            {/* Results Count */}
            <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>
                    {filteredProducts.length} produit{filteredProducts.length > 1 ? 's' : ''} trouvé{filteredProducts.length > 1 ? 's' : ''}
                    {!showAllStock && ` • Page ${currentPage}/${totalPages || 1}`}
                </span>
                
                {/* Items per page */}
                {!showAllStock && (
                    <Select value={itemsPerPage.toString()} onValueChange={(v) => setItemsPerPage(parseInt(v))}>
                        <SelectTrigger className="w-28 h-7 text-xs">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="25">25 / page</SelectItem>
                            <SelectItem value="50">50 / page</SelectItem>
                            <SelectItem value="100">100 / page</SelectItem>
                            <SelectItem value="500">Tout afficher</SelectItem>
                        </SelectContent>
                    </Select>
                )}
            </div>
            
            {/* Table View */}
            {viewMode === 'table' ? (
                <div className="border rounded-lg overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-muted/50">
                                <TableHead className="w-[200px]">
                                    <button onClick={() => handleSort('name')} className="flex items-center gap-1 hover:text-foreground">
                                        Produit <SortIcon field="name" />
                                    </button>
                                </TableHead>
                                <TableHead className="w-[120px]">
                                    <button onClick={() => handleSort('category')} className="flex items-center gap-1 hover:text-foreground">
                                        Catégorie <SortIcon field="category" />
                                    </button>
                                </TableHead>
                                <TableHead className="w-[100px]">Type</TableHead>
                                <TableHead className="w-[80px] text-right">
                                    <button onClick={() => handleSort('stock')} className="flex items-center gap-1 hover:text-foreground ml-auto">
                                        Stock <SortIcon field="stock" />
                                    </button>
                                </TableHead>
                                <TableHead className="w-[60px]">Unité</TableHead>
                                {canViewCosts && (
                                    <TableHead className="w-[90px] text-right">
                                        <button onClick={() => handleSort('purchase_price')} className="flex items-center gap-1 hover:text-foreground ml-auto">
                                            P. Achat <SortIcon field="purchase_price" />
                                        </button>
                                    </TableHead>
                                )}
                                <TableHead className="w-[90px] text-right">
                                    <button onClick={() => handleSort('selling_price')} className="flex items-center gap-1 hover:text-foreground ml-auto">
                                        P. Vente <SortIcon field="selling_price" />
                                    </button>
                                </TableHead>
                                <TableHead className="w-[80px]">
                                    <button onClick={() => handleSort('status')} className="flex items-center gap-1 hover:text-foreground">
                                        Statut <SortIcon field="status" />
                                    </button>
                                </TableHead>
                                <TableHead className="w-[140px]">Stock rapide</TableHead>
                                <TableHead className="w-[50px]">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {paginatedProducts.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={10} className="h-32 text-center text-muted-foreground">
                                        Aucun produit trouvé
                                    </TableCell>
                                </TableRow>
                            ) : (
                                paginatedProducts.map(product => (
                                    <TableRow 
                                        key={product.id} 
                                        className={cn("h-10", product.stockStatus.rowColor)}
                                        data-testid={`product-row-${product.id}`}
                                    >
                                        <TableCell className="font-medium text-sm py-1">
                                            {product.name}
                                        </TableCell>
                                        <TableCell className="text-xs text-muted-foreground py-1">
                                            {product.categoryName || '-'}
                                        </TableCell>
                                        <TableCell className="py-1">
                                            <Badge variant="outline" className={cn("text-[10px] px-1.5 py-0", PRODUCT_TYPES[product.productType]?.color)}>
                                                {PRODUCT_TYPES[product.productType]?.label}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-right font-mono text-sm py-1">
                                            {parseFloat(product.stock_quantity_base || 0).toFixed(1)}
                                        </TableCell>
                                        <TableCell className="text-xs text-muted-foreground py-1">
                                            {product.unit_base || 'unité'}
                                        </TableCell>
                                        {canViewCosts && (
                                            <TableCell className="text-right text-xs py-1">
                                                {formatCurrency(product.purchase_price_cfa)}
                                            </TableCell>
                                        )}
                                        <TableCell className="text-right text-xs py-1">
                                            {product.is_sellable !== false && product.selling_price_cfa > 0 
                                                ? formatCurrency(product.selling_price_cfa) 
                                                : '-'}
                                        </TableCell>
                                        <TableCell className="py-1">
                                            <Badge className={cn("text-[10px] px-1.5 py-0", product.stockStatus.color)}>
                                                {product.stockStatus.label}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="py-1">
                                            <QuickStockInline 
                                                product={product} 
                                                onStockChange={handleQuickStockChange} 
                                            />
                                        </TableCell>
                                        <TableCell className="py-1">
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button variant="ghost" size="icon" className="h-7 w-7">
                                                        <MoreVertical className="w-4 h-4" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end" className="w-48">
                                                    <DropdownMenuItem onClick={() => openStockDialog(product, 'in')}>
                                                        <PlusCircle className="w-4 h-4 mr-2 text-green-600" />
                                                        Entrée
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem onClick={() => openStockDialog(product, 'out')}>
                                                        <MinusCircle className="w-4 h-4 mr-2 text-red-600" />
                                                        Sortie
                                                    </DropdownMenuItem>
                                                    <DropdownMenuSeparator />
                                                    {canEdit && (
                                                        <>
                                                            {/* Sous-menu: Changer TYPE (logique métier) */}
                                                            <DropdownMenuSub>
                                                                <DropdownMenuSubTrigger>
                                                                    <Zap className="w-4 h-4 mr-2" />
                                                                    Changer type
                                                                </DropdownMenuSubTrigger>
                                                                <DropdownMenuSubContent className="min-w-[180px]">
                                                                    <div className="px-2 py-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
                                                                        Nature du produit
                                                                    </div>
                                                                    {Object.entries(PRODUCT_TYPES).map(([key, config]) => (
                                                                        <DropdownMenuItem 
                                                                            key={key}
                                                                            onClick={() => handleChangeProductType(product, key)}
                                                                            className={cn(
                                                                                "text-xs",
                                                                                product.productType === key && "bg-muted"
                                                                            )}
                                                                        >
                                                                            <config.icon className="w-3.5 h-3.5 mr-2" />
                                                                            {config.label}
                                                                            {product.productType === key && (
                                                                                <CheckCircle2 className="w-3 h-3 ml-auto text-green-600" />
                                                                            )}
                                                                        </DropdownMenuItem>
                                                                    ))}
                                                                </DropdownMenuSubContent>
                                                            </DropdownMenuSub>
                                                            
                                                            {/* Sous-menu: Changer CATÉGORIE (organisation) */}
                                                            <DropdownMenuSub>
                                                                <DropdownMenuSubTrigger>
                                                                    <Package className="w-4 h-4 mr-2" />
                                                                    Changer catégorie
                                                                </DropdownMenuSubTrigger>
                                                                <DropdownMenuSubContent className="max-h-[300px] overflow-y-auto min-w-[200px]">
                                                                    <div className="px-2 py-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
                                                                        Organisation métier
                                                                    </div>
                                                                    {categories
                                                                        .filter(cat => cat.id && cat.name)
                                                                        .sort((a, b) => a.name.localeCompare(b.name))
                                                                        .map(cat => {
                                                                            const IconComponent = CATEGORY_ICONS[cat.id] || Package;
                                                                            const isCurrentCategory = product.category_id === cat.id;
                                                                            return (
                                                                                <DropdownMenuItem 
                                                                                    key={cat.id}
                                                                                    onClick={() => handleChangeCategoryId(product, cat.id)}
                                                                                    className={cn(
                                                                                        "text-xs",
                                                                                        isCurrentCategory && "bg-muted"
                                                                                    )}
                                                                                >
                                                                                    <IconComponent className="w-3.5 h-3.5 mr-2" />
                                                                                    {cat.name}
                                                                                    {isCurrentCategory && (
                                                                                        <CheckCircle2 className="w-3 h-3 ml-auto text-green-600" />
                                                                                    )}
                                                                                </DropdownMenuItem>
                                                                            );
                                                                        })}
                                                                </DropdownMenuSubContent>
                                                            </DropdownMenuSub>
                                                            
                                                            <DropdownMenuItem onClick={() => openEditDialog(product)}>
                                                                <Edit className="w-4 h-4 mr-2" />
                                                                Modifier
                                                            </DropdownMenuItem>
                                                        </>
                                                    )}
                                                    <DropdownMenuItem onClick={() => viewProductHistory(product)}>
                                                        <History className="w-4 h-4 mr-2" />
                                                        Historique
                                                    </DropdownMenuItem>
                                                    {canDelete && (
                                                        <>
                                                            <DropdownMenuSeparator />
                                                            <DropdownMenuItem 
                                                                onClick={() => handleDeleteProduct(product)}
                                                                className="text-red-600"
                                                            >
                                                                <Trash2 className="w-4 h-4 mr-2" />
                                                                Supprimer
                                                            </DropdownMenuItem>
                                                        </>
                                                    )}
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            ) : (
                /* Cards View */
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2">
                    {paginatedProducts.map(product => (
                        <Card 
                            key={product.id} 
                            className={cn("overflow-hidden", product.stockStatus.rowColor)}
                            data-testid={`product-card-${product.id}`}
                        >
                            <CardContent className="p-2">
                                <div className="flex items-start justify-between mb-1">
                                    <h3 className="font-medium text-xs leading-tight line-clamp-2">{product.name}</h3>
                                    <Badge className={cn("text-[8px] px-1 py-0 ml-1 shrink-0", product.stockStatus.color)}>
                                        {product.stockStatus.label}
                                    </Badge>
                                </div>
                                <p className="text-[10px] text-muted-foreground mb-1">{product.categoryName || '-'}</p>
                                <div className="flex items-center justify-between">
                                    <span className="font-mono text-sm font-bold">
                                        {parseFloat(product.stock_quantity_base || 0).toFixed(0)}
                                    </span>
                                    <span className="text-[10px] text-muted-foreground">{product.unit_base}</span>
                                </div>
                                <div className="flex items-center gap-1 mt-1">
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        className="h-6 w-6"
                                        onClick={() => handleQuickStockChange(product, 'out', 1)}
                                    >
                                        <MinusCircle className="w-3 h-3 text-red-600" />
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        className="h-6 w-6"
                                        onClick={() => handleQuickStockChange(product, 'in', 1)}
                                    >
                                        <PlusCircle className="w-3 h-3 text-green-600" />
                                    </Button>
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button variant="outline" size="icon" className="h-6 w-6 ml-auto">
                                                <MoreVertical className="w-3 h-3" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                            {canEdit && (
                                                <DropdownMenuItem onClick={() => openEditDialog(product)}>
                                                    <Edit className="w-4 h-4 mr-2" />
                                                    Modifier
                                                </DropdownMenuItem>
                                            )}
                                            <DropdownMenuItem onClick={() => viewProductHistory(product)}>
                                                <History className="w-4 h-4 mr-2" />
                                                Historique
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}
            
            {/* Pagination */}
            {!showAllStock && totalPages > 1 && (
                <div className="flex items-center justify-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="h-8"
                    >
                        <ChevronLeft className="w-4 h-4" />
                    </Button>
                    
                    <div className="flex items-center gap-1">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNum;
                            if (totalPages <= 5) {
                                pageNum = i + 1;
                            } else if (currentPage <= 3) {
                                pageNum = i + 1;
                            } else if (currentPage >= totalPages - 2) {
                                pageNum = totalPages - 4 + i;
                            } else {
                                pageNum = currentPage - 2 + i;
                            }
                            
                            return (
                                <Button
                                    key={pageNum}
                                    variant={currentPage === pageNum ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => setCurrentPage(pageNum)}
                                    className="h-8 w-8 p-0"
                                >
                                    {pageNum}
                                </Button>
                            );
                        })}
                    </div>
                    
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="h-8"
                    >
                        <ChevronRight className="w-4 h-4" />
                    </Button>
                </div>
            )}
            
            {/* Quick Stock Movement Dialog */}
            <Dialog open={quickStockDialogOpen} onOpenChange={setQuickStockDialogOpen}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-base">
                            {stockAction === 'in' ? (
                                <PlusCircle className="w-5 h-5 text-green-600" />
                            ) : (
                                <MinusCircle className="w-5 h-5 text-red-600" />
                            )}
                            {stockAction === 'in' ? 'Entrée stock' : 'Sortie stock'}
                        </DialogTitle>
                        <DialogDescription className="text-sm">
                            {selectedProduct?.name}
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-3 py-2">
                        <div className="bg-muted/50 p-2 rounded-lg text-center">
                            <span className="text-xs text-muted-foreground">Stock actuel: </span>
                            <span className="font-bold">
                                {parseFloat(selectedProduct?.stock_quantity_base || 0).toFixed(1)} {selectedProduct?.unit_base}
                            </span>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs">Quantité</Label>
                                <Input
                                    type="number"
                                    min="0.1"
                                    step="0.1"
                                    value={stockQuantity}
                                    onChange={(e) => setStockQuantity(e.target.value)}
                                    className="h-9"
                                />
                            </div>
                            <div>
                                <Label className="text-xs">Motif</Label>
                                <Select value={stockReason} onValueChange={setStockReason}>
                                    <SelectTrigger className="h-9">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {(stockAction === 'in' ? stockInReasons : stockOutReasons).map(r => (
                                            <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        
                        <div>
                            <Label className="text-xs">Notes (optionnel)</Label>
                            <Input
                                value={stockNotes}
                                onChange={(e) => setStockNotes(e.target.value)}
                                placeholder="Détails..."
                                className="h-9"
                            />
                        </div>
                        
                        <div className="bg-muted/50 p-2 rounded-lg text-center">
                            <span className="text-xs text-muted-foreground">Nouveau stock: </span>
                            <span className="font-bold">
                                {(
                                    parseFloat(selectedProduct?.stock_quantity_base || 0) +
                                    (stockAction === 'in' ? 1 : -1) * (parseFloat(stockQuantity) || 0)
                                ).toFixed(1)} {selectedProduct?.unit_base}
                            </span>
                        </div>
                    </div>
                    
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setQuickStockDialogOpen(false)} size="sm">
                            Annuler
                        </Button>
                        <Button 
                            onClick={handleStockMovement}
                            size="sm"
                            className={stockAction === 'in' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
                        >
                            {stockAction === 'in' ? 'Ajouter' : 'Retirer'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            
            {/* History Dialog */}
            <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
                <DialogContent className="max-w-md max-h-[80vh]">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-base">
                            <History className="w-5 h-5" />
                            Historique - {selectedProduct?.name}
                        </DialogTitle>
                    </DialogHeader>
                    
                    <ScrollArea className="h-[350px] pr-4">
                        {productHistory.length === 0 ? (
                            <div className="text-center py-8 text-muted-foreground text-sm">
                                Aucun mouvement enregistré
                            </div>
                        ) : (
                            <div className="space-y-1.5">
                                {productHistory.map((movement, i) => (
                                    <div 
                                        key={movement.id || i}
                                        className={cn(
                                            "flex items-center gap-2 p-2 rounded-lg border text-sm",
                                            movement.movement_type === 'entry' || movement.type === 'IN'
                                                ? 'bg-green-50 border-green-200' 
                                                : 'bg-red-50 border-red-200'
                                        )}
                                    >
                                        {movement.movement_type === 'entry' || movement.type === 'IN' ? (
                                            <PlusCircle className="w-4 h-4 text-green-600 shrink-0" />
                                        ) : (
                                            <MinusCircle className="w-4 h-4 text-red-600 shrink-0" />
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <span className="font-bold">
                                                {movement.movement_type === 'entry' || movement.type === 'IN' ? '+' : '-'}
                                                {movement.quantity || movement.quantity_base}
                                            </span>
                                            <span className="text-xs text-muted-foreground ml-1">
                                                {movement.reason || movement.notes || '-'}
                                            </span>
                                        </div>
                                        <span className="text-[10px] text-muted-foreground shrink-0">
                                            {formatDate(movement.created_at)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </ScrollArea>
                </DialogContent>
            </Dialog>
            
            {/* Product Edit/Create Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-base">
                            {editingProduct ? 'Modifier le produit' : 'Nouveau produit'}
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-3 py-2">
                        <div>
                            <Label className="text-xs">Nom *</Label>
                            <Input
                                value={formData.name}
                                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                placeholder="Nom du produit"
                                className="h-9"
                            />
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs">Catégorie</Label>
                                <Select 
                                    value={formData.category_id} 
                                    onValueChange={(v) => setFormData(prev => ({ ...prev, category_id: v }))}
                                >
                                    <SelectTrigger className="h-9">
                                        <SelectValue placeholder="Sélectionner..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {categories.map(cat => (
                                            <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-xs">Unité</Label>
                                <Select 
                                    value={formData.unit_base} 
                                    onValueChange={(v) => setFormData(prev => ({ ...prev, unit_base: v }))}
                                >
                                    <SelectTrigger className="h-9">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {UNIT_OPTIONS.map(u => (
                                            <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs">Stock initial</Label>
                                <Input
                                    type="number"
                                    value={formData.stock_quantity_base}
                                    onChange={(e) => setFormData(prev => ({ ...prev, stock_quantity_base: e.target.value }))}
                                    className="h-9"
                                />
                            </div>
                            <div>
                                <Label className="text-xs">Seuil alerte</Label>
                                <Input
                                    type="number"
                                    value={formData.alert_threshold_base}
                                    onChange={(e) => setFormData(prev => ({ ...prev, alert_threshold_base: e.target.value }))}
                                    className="h-9"
                                />
                            </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs">Prix d'achat (FCFA)</Label>
                                <Input
                                    type="number"
                                    value={formData.purchase_price_cfa}
                                    onChange={(e) => setFormData(prev => ({ ...prev, purchase_price_cfa: e.target.value }))}
                                    className="h-9"
                                />
                            </div>
                            <div>
                                <Label className="text-xs">Prix de vente (FCFA)</Label>
                                <Input
                                    type="number"
                                    value={formData.selling_price_cfa}
                                    onChange={(e) => setFormData(prev => ({ 
                                        ...prev, 
                                        selling_price_cfa: e.target.value,
                                        is_sellable: !!e.target.value && parseFloat(e.target.value) > 0
                                    }))}
                                    className="h-9"
                                    placeholder="0 = non vendable"
                                />
                            </div>
                        </div>
                        
                        <div>
                            <Label className="text-xs">Fournisseur (optionnel)</Label>
                            <Input
                                value={formData.supplier}
                                onChange={(e) => setFormData(prev => ({ ...prev, supplier: e.target.value }))}
                                placeholder="Nom du fournisseur"
                                className="h-9"
                            />
                        </div>
                    </div>
                    
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setDialogOpen(false)} size="sm">
                            Annuler
                        </Button>
                        <Button onClick={handleSaveProduct} size="sm">
                            {editingProduct ? 'Modifier' : 'Créer'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            
            {/* Quick Restock Dialog */}
            <Dialog open={quickRestockDialogOpen} onOpenChange={setQuickRestockDialogOpen}>
                <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-lg">
                            <Zap className="w-5 h-5 text-emerald-500" />
                            Réapprovisionnement Rapide
                        </DialogTitle>
                        <DialogDescription>
                            Ajoutez rapidement des quantités à vos produits. Les modifications seront enregistrées en un clic.
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="flex-1 overflow-y-auto py-2">
                        {productsByCategory.map(category => (
                            <div key={category.id} className="mb-4">
                                <div 
                                    className="sticky top-0 bg-background z-10 flex items-center gap-2 py-2 px-3 mb-2 rounded-lg"
                                    style={{ backgroundColor: category.color + '20', borderLeft: `4px solid ${category.color}` }}
                                >
                                    <span className="font-semibold text-sm">{category.name}</span>
                                    <Badge variant="secondary" className="text-xs">
                                        {category.products.length}
                                    </Badge>
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 px-1">
                                    {category.products.map(product => {
                                        const currentStock = parseFloat(product.stock_quantity_base) || 0;
                                        const isLowStock = product.stockStatus?.status === 'faible' || product.stockStatus?.status === 'rupture';
                                        const restockQty = restockItems[product.id] || 0;
                                        
                                        return (
                                            <div 
                                                key={product.id}
                                                className={cn(
                                                    "flex items-center justify-between p-2 rounded-lg border transition-colors",
                                                    isLowStock && "border-orange-300 bg-orange-50/50",
                                                    restockQty > 0 && "border-emerald-300 bg-emerald-50/50"
                                                )}
                                            >
                                                <div className="flex-1 min-w-0 mr-2">
                                                    <div className="flex items-center gap-1.5">
                                                        <span className="font-medium text-sm truncate">{product.name}</span>
                                                        {isLowStock && (
                                                            <AlertTriangle className="w-3.5 h-3.5 text-orange-500 shrink-0" />
                                                        )}
                                                    </div>
                                                    <div className="text-xs text-muted-foreground">
                                                        Stock: {currentStock} {product.unit_base}
                                                        {restockQty > 0 && (
                                                            <span className="text-emerald-600 font-medium"> → {currentStock + restockQty}</span>
                                                        )}
                                                    </div>
                                                </div>
                                                
                                                <div className="flex items-center gap-1 shrink-0">
                                                    <Button
                                                        variant="outline"
                                                        size="icon"
                                                        className="h-7 w-7"
                                                        onClick={() => handleRestockQuantityChange(product.id, -1)}
                                                        disabled={restockQty === 0}
                                                    >
                                                        <MinusCircle className="w-4 h-4" />
                                                    </Button>
                                                    <Input
                                                        type="number"
                                                        value={restockQty || ''}
                                                        onChange={(e) => handleRestockInputChange(product.id, e.target.value)}
                                                        className="w-14 h-7 text-center text-sm px-1"
                                                        placeholder="0"
                                                        min="0"
                                                    />
                                                    <Button
                                                        variant="outline"
                                                        size="icon"
                                                        className="h-7 w-7"
                                                        onClick={() => handleRestockQuantityChange(product.id, 1)}
                                                    >
                                                        <PlusCircle className="w-4 h-4" />
                                                    </Button>
                                                    <Button
                                                        variant="outline"
                                                        size="icon"
                                                        className="h-7 w-7 text-emerald-600"
                                                        onClick={() => handleRestockQuantityChange(product.id, 5)}
                                                        title="+5"
                                                    >
                                                        +5
                                                    </Button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                    
                    <DialogFooter className="border-t pt-4 gap-2">
                        <div className="flex-1 text-sm">
                            {Object.keys(restockItems).length > 0 && (
                                <span className="text-emerald-600 font-medium">
                                    {Object.keys(restockItems).length} produit(s) à réapprovisionner
                                </span>
                            )}
                        </div>
                        <Button 
                            variant="outline" 
                            onClick={() => {
                                setRestockItems({});
                                setQuickRestockDialogOpen(false);
                            }}
                        >
                            Annuler
                        </Button>
                        <Button 
                            onClick={submitQuickRestock}
                            disabled={Object.keys(restockItems).length === 0}
                            className="bg-emerald-600 hover:bg-emerald-700"
                        >
                            <Save className="w-4 h-4 mr-2" />
                            Valider ({Object.values(restockItems).reduce((a, b) => a + b, 0)} unités)
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ProductsPage;
